import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import cn from 'classnames';

import { abeStylists } from 'actions/stylist/beSalonStylists.action';
import { feVoucherPanelToggle } from 'actions/ui.actions';
import filterActiveStylistsWithColumn from 'bl/data/filterActiveStylistsWithColumn.bl';
import Button from 'components/form/Button.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import LINK from 'constants/link.const';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import getAnyOrNull from 'utils/accessors/getAnyOrNull.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { getCurrentWeekDateString } from 'utils/date/getCurrentWeekDateString.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';

import CN from './VoucherSearch.module.scss';

const VoucherSearch = (props) => {
  const { feVoucherPanelToggle, onChange, totalValue, validValue, totalCount, validCount } = props ?? {};

  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();
  const history = useHistory();
  const nav = navFor('VoucherSearch()', { history });

  const { qj: resultRead } = usePrepareReadOnly(abeStylists);

  const stylists = filterActiveStylistsWithColumn(resultRead ?? []);

  const isOneStylist = stylists.length === 1;
  const stylistId = isOneStylist && getAnyOrNull('[0].id', stylists);

  const dateString = getCurrentWeekDateString();
  const pattern = isOneStylist ? LINK.scheduleWeekOneStylist : LINK.salonToday;

  const handleGoToVoucherSellPanel = useCallback(() => {
    const url = linkTo({ pattern, params: { dateString, salonId, stylistId } });
    nav.push({ pathname: url });
    feVoucherPanelToggle();
  }, [dateString, feVoucherPanelToggle, nav, pattern, salonId, stylistId]);

  const eVouchers = linkTo({ pattern: LINK.salonOnlineEvouchers, params: { salonId }, fault: '#' });

  return (
    <div
      data-bem="VoucherSearch"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <span className={CN.title}>{t('voucherTracking.title')}</span>
      <div className={CN.flex}>
        <div className={CN.flexItem}>
          <div className={CN.container}>
            <div>
              <div className={CN.textTotal}>
                <Space>{t('voucherTracking.searchSection.totalOutstandingValue')}</Space>
                <span className={CN.focused}>
                  <CurrencySign value={totalValue} />
                </span>
              </div>
              <div className={CN.textTotal}>
                <Space>{t('voucherTracking.searchSection.validOutstandingValue')}</Space>
                <span className={CN.focused}>
                  <CurrencySign value={validValue} />
                </span>
              </div>
            </div>
            <div>
              <div className={CN.textTotal}>
                <Space>{t('voucherTracking.searchSection.totalOutstandingCount')}</Space>
                <span>{fromAnyToString(totalCount)}</span>
              </div>
              <div className={CN.textTotal}>
                <Space>{t('voucherTracking.searchSection.validOutstandingCount')}</Space>
                <span>{fromAnyToString(validCount)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className={CN.flexItem}>
          <div className={CN.inputContainer}>
            <input
              name="text"
              type="text"
              onChange={onChange}
              placeholder={t('voucherTracking.searchSection.searchPlaceholder')}
              className={CN.input}
            />
          </div>
          <div className={CN.buttonSpace}>
            <NavLink to={eVouchers}>
              <Button variant="primary" width="small">
                <span className={CN.textLink}>{t('voucherTracking.searchSection.egiftVoucherButton')}</span>
              </Button>
            </NavLink>
          </div>
          <div className={CN.buttonSpace}>
            <Button variant="primary" width="small" onClick={handleGoToVoucherSellPanel}>
              <span className={CN.textSize}>{t('voucherTracking.searchSection.sellVoucherButton')}</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const VoucherSearchHoc = connect(
  null,
  mapActionsToDispatchToProps({
    feVoucherPanelToggle,
  }),
)(VoucherSearch);

export default VoucherSearchHoc;
